import type { TPlayer } from 'video.js'
import type { AdState } from '|>/core/addons/smartlib'
import type { Settings } from './settings'

import { Events } from '|>/shared/events'
import { bind_ } from '|>/shared/fn'
import { on, register } from '|>/shared/vjs'
import { BaseButton } from '../base'
import { CLOSE_MENU_DELAY } from './lib'
import { LocalEvents } from './local-events'

/**
 * Control bar button to open settings menu
 */
@register
export class SettingsButton extends BaseButton {
  declare controlText_: string | undefined
  declare settings: Settings

  pressed_: boolean | undefined
  private closeTimeoutId_: number | null = null
  private isDesktop_: boolean

  constructor(player: TPlayer, options: any) {
    super(player, options)
    this.pressed(false)

    // Check if device supports hover (desktop)
    this.isDesktop_ = window.matchMedia('(hover: hover)').matches

    if (this.isDesktop_) {
      // Setup hover handlers for desktop
      this.handleMouseEnter = bind_(this, this.handleMouseEnter)
      this.handleMouseLeave = bind_(this, this.handleMouseLeave)
      this.el_.addEventListener('mouseenter', this.handleMouseEnter)
      this.el_.addEventListener('mouseleave', this.handleMouseLeave)
    }
  }

  @on(Events.Media.AdStateChanged)
  handleAdStateChange(adState: AdState) {
    if (adState.isAdPlaying) {
      // Close settings menu if open
      if (this.pressed()) {
        this.settings.menuContainer.trigger(LocalEvents.SettingsCloseMenu)
      }
    }
  }

  override handleClick() {
    this.clearCloseTimeout()

    if (this.pressed()) {
      this.settings.menuContainer.trigger(LocalEvents.SettingsCloseMenu)
    } else {
      this.settings.menuContainer.update()
      this.settings.menuContainer.trigger(LocalEvents.SettingsActivateMainMenu)
    }
  }

  handleMouseEnter() {
    // Clear any pending close timeout
    this.clearCloseTimeout()

    // Open menu if not already open
    if (!this.pressed()) {
      this.settings.menuContainer.update()
      this.settings.menuContainer.trigger(LocalEvents.SettingsActivateMainMenu)
    }
  }

  handleMouseLeave() {
    // Schedule close menu after 1.5 seconds when mouse leaves button
    this.scheduleClose()
  }

  scheduleClose() {
    this.clearCloseTimeout()
    this.closeTimeoutId_ = window.setTimeout(() => {
      if (this.pressed()) {
        this.settings.menuContainer.trigger(LocalEvents.SettingsCloseMenu)
      }
      this.closeTimeoutId_ = null
    }, CLOSE_MENU_DELAY)
  }

  clearCloseTimeout() {
    if (this.closeTimeoutId_ !== null) {
      window.clearTimeout(this.closeTimeoutId_)
      this.closeTimeoutId_ = null
    }
  }

  override dispose() {
    this.clearCloseTimeout()
    if (this.isDesktop_ && this.el_) {
      this.el_.removeEventListener('mouseenter', this.handleMouseEnter)
      this.el_.removeEventListener('mouseleave', this.handleMouseLeave)
    }
    super.dispose()
  }

  pressed(is?: boolean) {
    if (is == null) {
      return this.pressed_
    }
    this.pressed_ = is
    this.toggleClass('vjs-x-settings-button-pressed', is)
    this.player_.toggleClass('vjs-x-settings-button', is)
  }
}

SettingsButton.prototype.controlText_ = 'Settings'
SettingsButton.options = {
  className: 'vjs-x-settings-button',
}
